import { Link, useNavigate } from "react-router-dom";
import HeroSection from "@/pages/home/components/HeroSection";
import FeaturedCarousel from "@/pages/home/components/FeaturedCarousel";
import ArticleCard from "@/components/feature/ArticleCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { categories } from "@/mocks/categories";
import { topics } from "@/mocks/topics";
import { articles } from "@/mocks/articles";
import { formatDate, latestArticles } from "@/lib/content";

export default function Home() {
  const navigate = useNavigate();
  const latest = latestArticles(6);
  const featured = latest.slice(0, 3);

  const countOf = (type: string) => articles.filter((item) => item.category === type).length;

  const handleSearch = (keyword: string) => {
    navigate(keyword ? `/search?q=${encodeURIComponent(keyword)}` : "/search");
  };

  return (
    <div className="w-full">
      <HeroSection onSearch={handleSearch} />

      <FeaturedCarousel />

      {/* 分类快捷入口 */}
      <section className="mx-auto w-full max-w-[1280px] px-4 pt-16 md:px-8">
        <SectionHeading
          kicker="核心栏目"
          title="三大主题分类"
          subtitle="从法律条文到伦理框架，再到案例教学，按主题系统化地理解这一议题的边界与理由。"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((category) => (
            <Link
              key={category.type}
              to={`/category/${category.type}`}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-lift"
            >
              <div className="relative h-44 w-full overflow-hidden">
                <img
                  src={category.cover}
                  alt={`${category.name}分类配图`}
                  title={`${category.name} - 乱伦专题分类`}
                  className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-lg bg-background-50/90 text-primary-700 backdrop-blur">
                  <i className={`${category.icon} text-xl`} />
                </span>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-foreground-950">{category.name}</h3>
                  <span className="rounded-full bg-secondary-50 px-2.5 py-0.5 text-xs text-secondary-800">
                    {countOf(category.type)} 篇
                  </span>
                </div>
                <p className="flex-1 text-sm leading-relaxed text-foreground-600">{category.intro}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-700">
                  进入分类
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 重点推荐 */}
      <section className="mx-auto w-full max-w-[1280px] px-4 pt-16 md:px-8">
        <SectionHeading
          kicker="编辑推荐"
          title="重点内容精读"
          subtitle="挑选具有代表性的权威解读与深度分析，适合快速建立对这一议题的整体认识。"
        />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {featured.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      </section>

      {/* 最新文章 */}
      <section className="mx-auto w-full max-w-[1280px] px-4 pt-16 md:px-8">
        <SectionHeading
          kicker="最近更新"
          title="最新文章列表"
          subtitle="近期发布的法律法规解读、伦理评论与案例分享，按发布时间倒序排列。"
          actionLabel="查看全部"
          actionTo="/search"
        />
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {latest.map((article, index) => (
            <Link
              key={article.id}
              to={`/detail/${article.id}`}
              className={[
                "group flex flex-col gap-2 p-4 transition-colors hover:bg-background-100 sm:flex-row sm:items-center sm:gap-5 sm:px-6",
                index !== latest.length - 1 ? "border-b border-background-200" : "",
              ].join(" ")}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 font-heading text-sm font-bold text-primary-700">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                  {article.title}
                </h3>
                <p className="truncate text-xs text-foreground-500">{article.summary}</p>
              </div>
              <div className="flex shrink-0 items-center gap-3 text-xs text-foreground-500">
                <span className="rounded-full bg-background-100 px-2.5 py-0.5">{article.source}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" />
                  {formatDate(article.date)}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 推荐专题 */}
      <section className="mx-auto w-full max-w-[1280px] px-4 pt-16 md:px-8">
        <SectionHeading
          kicker="专题集合"
          title="相关推荐专题"
          subtitle="围绕社会规范、法律边界与未成年人保护等主题，将分散的内容串联为完整的学习路径。"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {topics.map((topic) => (
            <Link
              key={topic.id}
              to={`/detail/${topic.articleIds[0]}`}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-secondary-200 hover:shadow-lift"
            >
              <div className="relative h-32 w-full overflow-hidden">
                <img
                  src={topic.cover}
                  alt={`${topic.name}配图`}
                  title={`${topic.name} - 推荐专题`}
                  className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-lg bg-background-50/90 text-secondary-700 backdrop-blur">
                  <i className={`${topic.icon} text-lg`} />
                </span>
              </div>
              <div className="flex flex-1 flex-col p-4">
                <h3 className="mb-1.5 text-base font-bold text-foreground-950">{topic.name}</h3>
                <p className="flex-1 text-xs leading-relaxed text-foreground-600">{topic.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-secondary-700">
                  含 {topic.articleIds.length} 篇内容
                  <i className="ri-arrow-right-line" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 关于本站 */}
      <section className="mx-auto w-full max-w-[1280px] px-4 pt-16 md:px-8">
        <div className="grid grid-cols-1 items-center gap-8 rounded-lg border border-background-200 bg-background-100 p-6 md:grid-cols-2 md:p-10">
          <div>
            <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-information-line" />
              关于本站
            </span>
            <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">
              严谨、克制、可溯源的知识门户
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              本站致力于提供系统、详实的法律条文讲解与伦理讨论。所有内容均基于公开法律法规与公开研究资料整理，
              案例均为虚构或去标识化处理，不涉及任何真实个案隐私。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                了解我们的原则
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-50/60"
              >
                开始检索内容
                <i className="ri-search-line" />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <i className="ri-scales-3-line text-2xl text-primary-600" />
              <p className="mt-3 font-heading text-2xl font-bold text-foreground-950">{articles.length}</p>
              <p className="text-xs text-foreground-500">篇科普内容</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <i className="ri-folder-shield-2-line text-2xl text-accent-600" />
              <p className="mt-3 font-heading text-2xl font-bold text-foreground-950">100%</p>
              <p className="text-xs text-foreground-500">虚构/公开来源</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <i className="ri-book-open-line text-2xl text-secondary-600" />
              <p className="mt-3 font-heading text-2xl font-bold text-foreground-950">3</p>
              <p className="text-xs text-foreground-500">大主题分类</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <i className="ri-search-eye-line text-2xl text-primary-600" />
              <p className="mt-3 font-heading text-2xl font-bold text-foreground-950">全文</p>
              <p className="text-xs text-foreground-500">关键词检索</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}